import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  MessageSquare,
  Mail,
  Phone,
  Bell,
  Webhook,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
  CheckCircle2,
  Clock,
  XCircle,
  Send,
  PackageCheck,
} from 'lucide-react';
import { api } from '../lib/api';
import { AppLayout } from '../components/AppLayout';

// ─── Types ────────────────────────────────────────────────────────────────────

interface MessageItem {
  id: string;
  channel: string;
  subject: string | null;
  recipientAddress: string;
  status: string;
  requestedAt: string;
  sentAt: string | null;
  deliveredAt: string | null;
  failedAt: string | null;
}

interface MessagesResponse {
  items: MessageItem[];
  total: number;
  page: number;
  limit: number;
}

interface DeliveryEvent {
  id: string;
  event: string;
  providerEvent: string | null;
  metadata: Record<string, unknown> | null;
  occurredAt: string;
}

interface MessageDetail extends MessageItem {
  deliveryEvents: DeliveryEvent[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function ChannelIcon({ channel, size = 14 }: { channel: string; size?: number }) {
  const icons: Record<string, React.ReactNode> = {
    email: <Mail size={size} />,
    sms: <Phone size={size} />,
    push: <Bell size={size} />,
    webhook: <Webhook size={size} />,
  };
  return <span className="text-zinc-400">{icons[channel] ?? <Mail size={size} />}</span>;
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { cls: string; label: string; icon: React.ReactNode }> = {
    queued: { cls: 'bg-zinc-100 text-zinc-600', label: 'Queued', icon: <Clock size={10} /> },
    sent: { cls: 'bg-blue-50 text-blue-700', label: 'Sent', icon: <Send size={10} /> },
    delivered: {
      cls: 'bg-emerald-50 text-emerald-700',
      label: 'Delivered',
      icon: <CheckCircle2 size={10} />,
    },
    failed: { cls: 'bg-red-50 text-red-600', label: 'Failed', icon: <XCircle size={10} /> },
    bounced: { cls: 'bg-orange-50 text-orange-600', label: 'Bounced', icon: <XCircle size={10} /> },
  };
  const s = map[status] ?? { cls: 'bg-zinc-100 text-zinc-600', label: status, icon: null };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}
    >
      {s.icon}
      {s.label}
    </span>
  );
}

function EventIcon({ event }: { event: string }) {
  const map: Record<string, React.ReactNode> = {
    delivered: <CheckCircle2 size={14} className="text-emerald-500" />,
    bounced: <XCircle size={14} className="text-red-500" />,
    complained: <XCircle size={14} className="text-orange-500" />,
    opened: <PackageCheck size={14} className="text-blue-500" />,
    clicked: <PackageCheck size={14} className="text-indigo-500" />,
    unsubscribed: <XCircle size={14} className="text-amber-500" />,
    failed: <XCircle size={14} className="text-red-500" />,
  };
  return <>{map[event] ?? <Clock size={14} className="text-zinc-400" />}</>;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ─── Message detail panel ─────────────────────────────────────────────────────

function MessageDetailPanel({ messageId, onClose }: { messageId: string; onClose: () => void }) {
  const [detail, setDetail] = useState<MessageDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    api
      .get<MessageDetail>(`/customer/messages/${messageId}`)
      .then((res) => setDetail(res.data))
      .catch(() => setError('Failed to load message details.'))
      .finally(() => setLoading(false));
  }, [messageId]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-xl flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
          <h3 className="text-sm font-semibold text-zinc-900">Message detail</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-zinc-400 hover:text-zinc-700 transition-colors rounded-lg hover:bg-zinc-100"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={20} className="animate-spin text-zinc-400" />
            </div>
          ) : error ? (
            <p className="text-sm text-red-500">{error}</p>
          ) : detail ? (
            <div className="space-y-5">
              {/* Meta */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <ChannelIcon channel={detail.channel} size={16} />
                  <span className="text-xs text-zinc-500 capitalize">{detail.channel}</span>
                  <StatusBadge status={detail.status} />
                </div>
                <h2 className="text-base font-semibold text-zinc-900">
                  {detail.subject ?? `${detail.channel} message`}
                </h2>
                <p className="text-xs text-zinc-400">
                  Sent to:{' '}
                  <span className="text-zinc-600 font-medium">{detail.recipientAddress}</span>
                </p>
                <p className="text-xs text-zinc-400">Requested: {formatDate(detail.requestedAt)}</p>
                {detail.sentAt && (
                  <p className="text-xs text-zinc-400">Sent: {formatDate(detail.sentAt)}</p>
                )}
                {detail.deliveredAt && (
                  <p className="text-xs text-zinc-400">
                    Delivered: {formatDate(detail.deliveredAt)}
                  </p>
                )}
              </div>

              {/* Delivery timeline */}
              {detail.deliveryEvents.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-3">
                    Delivery events
                  </h4>
                  <div className="space-y-2">
                    {detail.deliveryEvents.map((ev) => (
                      <div
                        key={ev.id}
                        className="flex items-center gap-3 p-3 rounded-xl bg-zinc-50"
                      >
                        <EventIcon event={ev.event} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-zinc-800 capitalize">{ev.event}</p>
                          {ev.providerEvent && (
                            <p className="text-xs text-zinc-400">{ev.providerEvent}</p>
                          )}
                        </div>
                        <p className="text-xs text-zinc-400 flex-shrink-0">
                          {formatDate(ev.occurredAt)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {detail.deliveryEvents.length === 0 && (
                <p className="text-sm text-zinc-400 text-center py-4">
                  No delivery events recorded yet.
                </p>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const PAGE_LIMIT = 20;

export function MessagesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [messages, setMessages] = useState<MessageItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const selectedId = searchParams.get('id');

  useEffect(() => {
    setLoading(true);
    setError('');
    api
      .get<MessagesResponse>('/customer/messages', { params: { page, limit: PAGE_LIMIT } })
      .then((res) => {
        setMessages(res.data.items ?? []);
        setTotal(res.data.total ?? 0);
      })
      .catch(() => setError('Failed to load messages.'))
      .finally(() => setLoading(false));
  }, [page]);

  const totalPages = Math.ceil(total / PAGE_LIMIT);

  const closeDetail = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('id');
      return next;
    });
  };

  const openDetail = (id: string) => {
    setSearchParams({ id });
  };

  return (
    <AppLayout>
      <div className="max-w-2xl space-y-6">
        <div>
          <h1 className="text-xl font-bold text-zinc-900 flex items-center gap-2">
            <MessageSquare size={20} className="text-[--color-primary]" />
            Messages
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            A history of all communications we've sent to you.
          </p>
        </div>

        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 px-5 py-4 text-sm text-red-600">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 size={24} className="animate-spin text-zinc-400" />
          </div>
        ) : messages.length === 0 ? (
          <div className="bg-white border border-zinc-100 rounded-2xl shadow-sm px-6 py-12 text-center">
            <MessageSquare size={32} className="mx-auto text-zinc-300 mb-3" />
            <p className="text-sm font-medium text-zinc-500">No messages yet</p>
            <p className="text-xs text-zinc-400 mt-1">Messages we send you will appear here.</p>
          </div>
        ) : (
          <>
            <div className="bg-white border border-zinc-100 rounded-2xl shadow-sm divide-y divide-zinc-50 overflow-hidden">
              {messages.map((msg) => (
                <button
                  key={msg.id}
                  type="button"
                  onClick={() => openDetail(msg.id)}
                  className="w-full flex items-center gap-3 px-5 py-4 hover:bg-zinc-50 transition-colors text-left"
                >
                  <ChannelIcon channel={msg.channel} size={16} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-zinc-800 truncate">
                      {msg.subject ?? `${msg.channel} message`}
                    </p>
                    <p className="text-xs text-zinc-400 mt-0.5">
                      {new Date(msg.requestedAt).toLocaleDateString(undefined, {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </p>
                  </div>
                  <StatusBadge status={msg.status} />
                  <ChevronRight size={14} className="text-zinc-300 flex-shrink-0" />
                </button>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-xs text-zinc-400">
                  Showing {(page - 1) * PAGE_LIMIT + 1}–{Math.min(page * PAGE_LIMIT, total)} of{' '}
                  {total}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                    className="p-1.5 rounded-lg border border-zinc-200 text-zinc-500 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <span className="text-xs text-zinc-600 font-medium">
                    {page} / {totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="p-1.5 rounded-lg border border-zinc-200 text-zinc-500 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {selectedId && <MessageDetailPanel messageId={selectedId} onClose={closeDetail} />}
    </AppLayout>
  );
}
